"use client";

import { Fragment } from "react";
import { Session } from "next-auth";
import { SessionProvider, useSession } from "next-auth/react";
import PostHogSessionIdentity from "@/components/layout/analytics/PostHogSessionIdentity";
import { setVehicleSearchOwner } from "@/lib/vehicle/corporateSearch";

type Props = {
  children: React.ReactNode;
  session?: Session | null;
};

function VehicleSearchSessionBoundary({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: session, status } = useSession();
  // Loading is not logout: retain the private snapshot until identity resolves.
  if (status !== "loading" && typeof window !== "undefined") {
    setVehicleSearchOwner(session?.user?.id ?? null);
  }
  return children;
}

/** Only car content waits for identity; public pages never disappear during auth loading. */
export function VehicleSearchPrivateBoundary({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: session, status } = useSession();
  if (status === "loading")
    return <div role="status">Loading your car search…</div>;
  const owner = session?.user?.id ?? null;
  // Synchronous scoping prevents a new owner reading the previous owner's code
  // on the first render, before any post-render cleanup could run.
  if (typeof window !== "undefined") setVehicleSearchOwner(owner);
  return <Fragment key={owner ?? "anonymous"}>{children}</Fragment>;
}

export default function ClientProvider({
  children,
  session,
}: Props): React.ReactNode {
  return (
    <SessionProvider session={session}>
      <PostHogSessionIdentity>
        {session === undefined ? (
          children
        ) : (
          <VehicleSearchSessionBoundary>
            {children}
          </VehicleSearchSessionBoundary>
        )}
      </PostHogSessionIdentity>
    </SessionProvider>
  );
}
